/**
 * §F4 splash mini-map — country outlines and, since the client review's R2, the eleven
 * intervention watersheds shaded by activity count.
 *
 * **What changed and why.** §F4 shipped one pulsing dot per org unit the location resolver
 * had landed an activity on: 65 markers, two thirds of them communes, clustered so tightly
 * around Bobo-Dioulasso that they occluded each other. The review asked for the programme's
 * own geography instead — a choropleth of the 11 basins the programme is organised by, each
 * carrying the roll-up of *all* its activities (`?by=watershed`, NOTES #480), labelled with
 * its count. A reader now sees eleven shapes and eleven numbers rather than a scatter whose
 * density is a function of how finely MELS happened to geocode a row.
 *
 * **A second, independent `L.Map`.** `features/map/mapContext` publishes the *one* instance
 * `MapView` owns for the routed Map Explorer, and this is not it: the splash sits over the
 * live app, both maps are on screen at once, and sharing an instance would mean the
 * Explorer's basemap, panes, layers and viewport were also the splash's. So this component
 * owns its own map, its own tile layer and its own overlays, and disposes of all of them
 * when the splash closes.
 *
 * Everything it draws comes from three public endpoints — `/hierarchy/units?level=0` for
 * the outlines, `/hierarchy/units?type=watershed` for the basins and
 * `/activities/points?by=watershed` for the counts — so it renders before anybody has
 * signed in, which is the whole point of a landing page.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';

import { fetchActivityPoints, fetchUnits, queryKeys, type ActivityPointsFC, type UnitsFC } from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { escapeHtml } from '../../lib/html';
import { COUNTRY_NAMES, useAppStore, type Country } from '../../lib/state';
import { BASEMAPS } from '../map/basemaps';
import { HOME_BOUNDS } from '../map/panes';
import {
  markerCountry,
  markerTooltip,
  watershedActivity,
  watershedFill,
  watershedRows,
  watershedShading,
  WATERSHED_NODATA_FILL,
} from './activityPoints';
import { Ticker } from './Ticker';

/** What `L.geoJSON` will take, without importing a `@types/geojson` we do not have. */
type GeoJsonInput = Parameters<typeof L.geoJSON>[0];

/**
 * Outline style — the mockup's brand-green hairline over a 5% wash, **stepped back**.
 *
 * §F4 gave the borders 1.8px at full opacity, which was right when they were the only
 * geometry on the map. Since R2 the eleven basins are the subject and the four countries
 * are the context: same colour, thinner and softer, so they still say where Nigeria ends
 * without competing with a shape a tenth their size.
 */
const OUTLINE_STYLE: L.PathOptions = {
  color: '#2E7D32',
  weight: 1.4,
  opacity: 0.75,
  fillColor: '#2E7D32',
  fillOpacity: 0.045,
};

/**
 * The basin polygons' stroke: a dark green hairline, so a basin reads as an object at
 * postage-stamp size whichever end of the ramp it is filled from — the palest class is
 * nearly white and would otherwise dissolve into the basemap.
 */
const WATERSHED_STROKE: L.PathOptions = {
  color: '#14400F',
  weight: 1.3,
  opacity: 0.9,
  fillOpacity: 0.92,
};

export interface MiniMapProps {
  /** Clicking a watershed: adopt its country and leave. */
  onPick: (country: Country) => void;
}

export function MiniMap({ onPick }: MiniMapProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  /** The scope the viewport was last framed for — see the `fitBounds` below. */
  const fittedFor = useRef<string | null>(null);
  const [map, setMap] = useState<L.Map | null>(null);
  const basemap = useAppStore((s) => s.basemap);
  const country = useAppStore((s) => s.country);

  const { data: outlines } = useQuery<UnitsFC>({
    // `z5`, the coarsest generalisation: four country rings at postage-stamp size. `z7`
    // is the app-wide default and would ship several times the geometry to draw the same
    // four shapes — on the one view that is a visitor's first paint.
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z5'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z5'),
  });
  const { data: basins } = useQuery<UnitsFC>({
    // The same argument, and the numbers are on the record: eleven watersheds are 9.6 kB at
    // `z5` and 29 kB at `z7`, for a 320 px map (backend hand-off, NOTES #480).
    queryKey: queryKeys.units('watershed', null, country, 'z5'),
    queryFn: () => fetchUnits('watershed', null, country, 'z5'),
  });
  const { data: points } = useQuery<ActivityPointsFC>({
    queryKey: queryKeys.activityPoints(country, 'watershed'),
    queryFn: () => fetchActivityPoints(country, 'watershed'),
  });

  // --- the map itself, created once ------------------------------------------
  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;

    // `zoomSnap: 0.25` — the eleven basins are a wide, short band (17° of longitude, 4.5°
    // of latitude) inside a tall panel, and at Leaflet's default integer snap the best fit
    // rounds *down* a whole level and throws away 40% of the linear scale the panel could
    // give them. A fractional zoom is free on a map with no tile-alignment obligations.
    const m = L.map(el, { zoomControl: false, attributionControl: true, zoomSnap: 0.25 });
    m.attributionControl.setPrefix('Soil Values Geoportal');
    L.control.zoom({ position: 'bottomright' }).addTo(m);
    m.fitBounds(HOME_BOUNDS);
    mapRef.current = m;
    setMap(m);

    return () => {
      m.remove();
      mapRef.current = null;
      setMap(null);
    };
  }, []);

  /**
   * Leaflet reads the container size once, at construction. The splash card is laid out by
   * a CSS grid inside a flex overlay, and on the first paint after mount that container is
   * frequently still zero-height — which leaves the map convinced it is 0×0 and drawing no
   * tiles at all. The mockup papered over this with `setTimeout(…, 90)`; a `ResizeObserver`
   * is the version that is also right when the window is resized with the splash open.
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!map || !el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    observer.observe(el);
    return () => observer.disconnect();
  }, [map]);

  /**
   * Basemap, **kept in sync with the store** rather than read once at mount.
   *
   * The mockup builds its tile layer inside an `if (!homeMap)` guard, so a reader who
   * switched the Explorer to satellite and then reopened the Overview got a light-grey
   * mini-map of a satellite app. §F4 says "basemap per state"; this is that, continuously.
   */
  useEffect(() => {
    if (!map) return;
    const def = BASEMAPS[basemap];
    const layer = L.tileLayer(def.url, { attribution: def.attr }).addTo(map);
    layer.bringToBack();
    return () => {
      map.removeLayer(layer);
    };
  }, [map, basemap]);

  // --- country outlines ------------------------------------------------------
  useEffect(() => {
    if (!map || !outlines) return;
    const layer = L.geoJSON(outlines as unknown as GeoJsonInput, {
      style: () => OUTLINE_STYLE,
      interactive: false, // the basins are the only thing to click here
    }).addTo(map);
    return () => {
      map.removeLayer(layer);
    };
  }, [map, outlines]);

  /**
   * The join, once per payload pair rather than once per render: the Leaflet effect and the
   * legend below are two readings of the same eleven rows, and computing them separately is
   * how a legend ends up describing a classification the map did not use.
   */
  const rows = useMemo(() => watershedRows(basins, watershedActivity(points)), [basins, points]);
  const shading = useMemo(() => watershedShading(rows.map((r) => r.count)), [rows]);

  // --- watershed choropleth + count labels -----------------------------------
  useEffect(() => {
    if (!map || !basins) return;
    const group = L.layerGroup().addTo(map);

    const byId = new Map(rows.map((r) => [r.unit_id, r]));

    /** `"224 activities · Kou · Burkina Faso"` — USER INTENT's sentence, verbatim. */
    const tooltipFor = (unitId: string): string => {
      const row = byId.get(unitId);
      if (!row) return '';
      const countryName = row.country
        ? t(`country.${row.country}`, COUNTRY_NAMES[row.country as Country] ?? row.country)
        : '';
      return markerTooltip(
        t('splash.map.markerCount', { count: row.count, value: fmtNum(row.count, 0) }),
        row.name,
        countryName,
      );
    };

    /**
     * §F4's contract was "click → close splash, set country, fly Map Explorer", and R2
     * moves it from a dot to a polygon without changing a word of it: the splash owns all
     * three (`features/landing/index.tsx::pickCountry`), and the fly is a *consequence* of
     * the country change that `features/map/HierarchyLayers`' `CountryFocus` performs once
     * the Explorer route is on screen.
     */
    const pick = (unitId: string) => {
      const row = byId.get(unitId);
      const iso3 = row && markerCountry(row);
      if (iso3) onPick(iso3);
    };

    const shapes = L.geoJSON(basins as unknown as GeoJsonInput, {
      style: (feature) => {
        const id = String((feature?.properties as { id?: string })?.id ?? '');
        return {
          ...WATERSHED_STROKE,
          fillColor: watershedFill(byId.get(id)?.count ?? 0, shading),
        };
      },
      onEachFeature: (feature, layer) => {
        const id = String((feature?.properties as { id?: string })?.id ?? '');
        if (!byId.has(id)) return;
        layer.bindTooltip(escapeHtml(tooltipFor(id)), { className: 'unit-tip', sticky: true });
        layer.on('click', () => pick(id));
        // The same `data-` hooks the markers carried, for the e2e suite and for anyone
        // debugging a mis-shaded basin: the tooltip is otherwise the only place a polygon
        // says which unit it is, and reading it requires a hover. The `add` handler is
        // where the SVG element first exists — `onEachFeature` runs during construction,
        // before the layer has a renderer.
        layer.on('add', () => {
          const el = layer instanceof L.Path ? layer.getElement() : null;
          if (!el) return;
          el.setAttribute('data-unit', id);
          el.setAttribute('data-count', String(byId.get(id)?.count ?? 0));
          el.classList.add('ws-shape');
        });
      },
    }).addTo(group);
    shapes.bringToFront();

    // The printed counts, added biggest-first (`watershedRows` sorts) so a busy basin's
    // number is drawn over a quiet neighbour's rather than under it.
    for (const row of rows) {
      if (!row.centroid) continue; // shaded and tooltipped, just unlabelled — see WatershedActivity
      const [lng, lat] = row.centroid;
      const marker = L.marker([lat, lng], {
        icon: L.divIcon({
          className: '',
          html:
            `<span class="ws-count" data-unit="${escapeHtml(row.unit_id)}"` +
            ` data-count="${row.count}">${escapeHtml(fmtNum(row.count, 0))}</span>`,
          iconSize: [40, 20],
          iconAnchor: [20, 10],
        }),
        keyboard: false,
        title: '', // the bound tooltip is the accessible name; a native one would double it
      });
      // Interactive, unlike a decoration: the number *is* the basin as far as a reader
      // aiming at it is concerned, so it carries the same tooltip and the same click.
      marker.bindTooltip(escapeHtml(tooltipFor(row.unit_id)), {
        className: 'unit-tip',
        sticky: true,
      });
      marker.on('click', () => pick(row.unit_id));
      marker.addTo(group);
    }

    /**
     * Frame the basins, not the continent.
     *
     * `HOME_BOUNDS` is 21° of latitude — the right extent for a map of four countries, and
     * the wrong one for a map *of eleven watersheds*: at that scale the shapes are a few
     * pixels of green inside the outlines and their eleven numbers pile into one another
     * over central Burkina. The programme's own geography is what R2 asks the splash to
     * show, so the mini-map opens on it and the country outlines run off the edges as the
     * context they now are.
     *
     * Once per scope, not once per payload: `points`/`basins` can refetch (window focus,
     * a language switch elsewhere), and yanking the viewport back from under a reader who
     * has panned would be a bug with no symptom in a test.
     */
    const bounds = shapes.getBounds();
    if (bounds.isValid() && fittedFor.current !== country) {
      // The payload usually lands before the grid has given this container its height, and
      // `fitBounds` against a container Leaflet still believes is 0x0 picks a zoom for a map
      // that does not exist — which looked exactly like the fit not happening at all.
      map.invalidateSize({ animate: false });
      map.fitBounds(bounds, { padding: [26, 26], animate: false });
      fittedFor.current = country;
    }

    return () => {
      map.removeLayer(group);
    };
  }, [map, basins, rows, shading, country, onPick, t]);

  const unassigned = points?.unassigned ?? 0;
  const anyEmpty = rows.some((r) => r.count === 0);

  return (
    <div id="landMapSlot">
      <div id="homemap" ref={containerRef} data-testid="splash-map" />
      {/* The legend is the half of a choropleth that makes it readable: without it the
          eleven greens are a decoration. Kept to a strip, two numbers and a caption —
          §F5.6's full legend belongs to the Map Explorer, not to a 320px first paint. */}
      <div id="hpMapKey" className="glass" data-testid="splash-map-key">
        <b>{t('splash.map.keyTitle')}</b>
        <div className="hp-key-strip" aria-hidden="true">
          {shading.colors.map((color, i) => (
            <i key={i} style={{ background: color }} />
          ))}
        </div>
        <div className="hp-key-scale">
          <span>{shading.hasData ? fmtNum(shading.min, 0) : '—'}</span>
          <span>{shading.hasData ? fmtNum(shading.max, 0) : '—'}</span>
        </div>
        {/* Only when a basin actually has none. Today all eleven are non-zero, so this
            stays out of a 320px map until the day the sentence is true. */}
        {anyEmpty && (
          <div className="hp-key-note">
            <i style={{ background: WATERSHED_NODATA_FILL }} />
            {t('splash.map.keyNone')}
          </div>
        )}
        {/* NOTES #480: 76 of 1,646 non-draft rows carry one of MELS' "Tous les bassins
            versants" pseudo-watersheds or none at all. A choropleth that did not say so
            would read as the whole programme and be 5% short. */}
        {unassigned > 0 && (
          <div className="hp-key-note" data-testid="splash-unassigned">
            {t('splash.map.unassigned', {
              count: unassigned,
              value: fmtNum(unassigned, 0),
            })}
          </div>
        )}
      </div>
      <Ticker />
    </div>
  );
}

export default MiniMap;
